import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FlaskConical, ShieldCheck, Sparkles, Syringe } from "lucide-react";
import { Reveal, Overline } from "@/components/Motion";
import { IMAGES, PRESTATION_GROUPS, CONTACT, PLANITY_URL } from "@/data/maison";

const PROTOCOLE = [
  {
    icon: ShieldCheck,
    title: "Hygiène irréprochable",
    text: "Studio dédié, protocole strict et gestuelle maîtrisée avant chaque acte.",
  },
  {
    icon: FlaskConical,
    title: "Pigments certifiés REACH",
    text: "Des pigments stables et sûrs, conformes à la réglementation européenne.",
  },
  {
    icon: Syringe,
    title: "Aiguilles à usage unique",
    text: "Matériel stérile ouvert devant vous, jamais réutilisé.",
  },
  {
    icon: Sparkles,
    title: "Sublimer, sans transformer",
    text: "Chaque acte débute par une analyse morphologique et un échange sur vos attentes.",
  },
];

export default function Studio() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pt-16 md:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Reveal y={16}>
            <Overline>Le Studio — {CONTACT.address}</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-light tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              L'expertise du regard, en{" "}
              <span className="font-script text-[1.1em] text-primary">studio privé</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-lg text-base md:text-lg font-light leading-relaxed text-muted-foreground">
              Maquillage permanent des sourcils, des lèvres et du visage,
              tricopigmentation et soins avancés de la peau : chaque prestation
              débute par une analyse morphologique et un échange sur vos
              attentes.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:col-span-5">
          <div className="overflow-hidden border border-border">
            <img
              src={IMAGES.studioInterior}
              alt="Le studio Maison Ria — écrin parisien"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
        </Reveal>
      </section>

      {/* Prestations */}
      <section className="border-y border-border bg-white/60">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24" data-testid="studio-prestations">
          <Reveal>
            <Overline>La carte des soins</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl">
              Les prestations
            </h2>
          </Reveal>

          {PRESTATION_GROUPS.map((group) => (
            <div key={group.id} id={group.id} className="mt-16 scroll-mt-28" data-testid={`prestation-group-${group.id}`}>
              <Reveal>
                <h3 className="text-xs tracking-[0.28em] uppercase font-semibold text-accent border-b border-accent/40 pb-4">
                  {group.title}
                </h3>
              </Reveal>
              {group.items.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.04}>
                  <div className="group grid md:grid-cols-12 gap-4 md:gap-8 items-center py-8 border-b border-border transition-colors duration-300 hover:bg-secondary/50 md:px-4">
                    <div className="md:col-span-4">
                      <h4 className="font-display text-2xl font-light">{p.name}</h4>
                      <p className="mt-2 text-sm text-muted-foreground">{p.duration}</p>
                    </div>
                    <div className="md:col-span-5">
                      <p className="text-sm font-light leading-relaxed text-muted-foreground">
                        {p.desc}
                      </p>
                      {p.note && (
                        <p className="mt-3 text-xs text-muted-foreground/80 leading-relaxed">
                          {p.note}
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-3 flex items-center justify-between md:justify-end gap-6">
                      <span className="font-display text-xl whitespace-nowrap">{p.price}</span>
                      {p.devis ? (
                        <a
                          data-testid={`prestation-devis-${p.id}`}
                          href={`tel:${CONTACT.phoneRaw}`}
                          className="text-xs tracking-[0.18em] uppercase border border-foreground px-5 py-3 transition-colors duration-300 hover:bg-foreground hover:text-background"
                        >
                          Devis
                        </a>
                      ) : (
                        <a
                          data-testid={`prestation-book-${p.id}`}
                          href="#reservation"
                          className="text-xs tracking-[0.18em] uppercase border border-foreground px-5 py-3 transition-colors duration-300 group-hover:bg-foreground group-hover:text-background"
                        >
                          Réserver
                        </a>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ))}

          <Reveal className="mt-10">
            <p className="text-sm text-muted-foreground font-light max-w-2xl leading-relaxed">
              Vous avez une ancienne pigmentation ? Merci de me contacter
              directement au{" "}
              <a href={`tel:${CONTACT.phoneRaw}`} className="underline underline-offset-4 decoration-accent hover:text-foreground transition-colors duration-300">
                {CONTACT.phone}
              </a>{" "}
              avant de réserver.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Résultat + Protocole */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <div className="overflow-hidden rounded-t-[999px] border border-border">
            <img
              src={IMAGES.resultsMacro}
              alt="Résultat nanoblading — poil à poil"
              className="w-full aspect-[3/4] object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <Reveal>
            <Overline>Le protocole Maison Ria</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl leading-tight">
              Rassurer, avant de sublimer.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-10">
            {PROTOCOLE.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <item.icon size={28} strokeWidth={1} className="text-accent" />
                <h3 className="mt-4 font-display text-xl">{item.title}</h3>
                <p className="mt-2 text-sm font-light text-muted-foreground leading-relaxed">
                  {item.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Réservation Planity */}
      <section id="reservation" className="scroll-mt-24 bg-secondary/60 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32" data-testid="studio-reservation">
          <Reveal>
            <Overline>Réservation en ligne</Overline>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl">
              Choisissez votre créneau.
            </h2>
            <p className="mt-6 max-w-xl font-light text-muted-foreground">
              Réservez directement ci-dessous via Planity, notre partenaire de
              réservation. Confirmation immédiate, rappel avant votre séance.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12">
            <div className="border border-border bg-white p-2 md:p-4 shadow-[0_20px_40px_rgba(46,33,21,0.05)]">
              <iframe
                data-testid="planity-iframe"
                src={PLANITY_URL}
                title="Réservation Maison Ria — Planity"
                className="w-full border-0"
                style={{ minHeight: 700 }}
                loading="lazy"
              />
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Le module ne s'affiche pas ?{" "}
              <a
                data-testid="planity-fallback-link"
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 decoration-accent hover:text-foreground transition-colors duration-300"
              >
                Réservez directement sur Planity
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
